import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { allProducts } from '@/mocks/products';
import {
  SITE_URL,
  SITE_NAME,
  FAVICON_URL,
  HOME_OG_IMAGE,
  ARTICLE_OG_IMAGE,
  SUPPORTED_LANGS,
  HOME_SEO,
  ARTICLE_SEO,
  type SupportedLang,
} from './data';
import {
  applyHead,
  applyNotFound,
  clearJsonLd,
  injectJsonLd,
  type HeadData,
} from './head';

type RouteType = 'home' | 'article' | 'product' | 'notfound';

interface ResolvedRoute {
  type: RouteType;
  lang: SupportedLang;
  canonicalPath: string;
  productId?: string;
}

function resolveRoute(pathname: string): ResolvedRoute {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { type: 'home', lang: 'en', canonicalPath: '/' };
  }

  const lang = (SUPPORTED_LANGS.includes(segments[0] as SupportedLang)
    ? segments[0]
    : 'en') as SupportedLang;

  if (segments.length === 1) {
    return { type: 'home', lang, canonicalPath: `/${segments[0]}/` };
  }

  if (segments.length === 2 && segments[1] === 'allchinabuy-spreadsheet') {
    return {
      type: 'article',
      lang,
      canonicalPath: `/${segments[0]}/allchinabuy-spreadsheet/`,
    };
  }

  if (segments.length === 3 && segments[1] === 'product') {
    return {
      type: 'product',
      lang,
      canonicalPath: `/${segments[0]}/product/${segments[2]}`,
      productId: segments[2],
    };
  }

  return { type: 'notfound', lang, canonicalPath: pathname };
}

function applyHome(lang: SupportedLang, canonicalPath: string) {
  const seo = HOME_SEO[lang];
  const canonical = `${SITE_URL}${canonicalPath}`;

  const head: HeadData = {
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical,
    ogType: 'website',
    ogImage: HOME_OG_IMAGE,
    ogImageAlt: seo.title,
    locale: seo.locale,
  };

  applyHead(head);

  injectJsonLd('seo-website', {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: seo.description,
    inLanguage: lang,
  });

  injectJsonLd('seo-organization', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: FAVICON_URL,
  });
}

function applyArticle(lang: SupportedLang, canonicalPath: string) {
  const seo = ARTICLE_SEO[lang];
  const canonical = `${SITE_URL}${canonicalPath}`;

  const head: HeadData = {
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical,
    ogType: 'article',
    ogImage: ARTICLE_OG_IMAGE,
    ogImageAlt: seo.title,
    locale: seo.locale,
  };

  applyHead(head);

  injectJsonLd('seo-article', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: seo.title,
    description: seo.description,
    image: ARTICLE_OG_IMAGE,
    inLanguage: lang,
    datePublished: '2026-08-25',
    dateModified: '2026-08-25',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
  });
}

function applyProduct(
  lang: SupportedLang,
  canonicalPath: string,
  productId: string,
) {
  const product = allProducts.find((p) => p.id === productId);

  if (!product) {
    applyNotFound();
    return;
  }

  const canonical = `${SITE_URL}${canonicalPath}`;
  const title = `${product.name} — ${SITE_NAME}`;
  const description = `${product.name} in ${product.category}. Price ¥${product.priceYuan} (≈ $${product.priceUsd.toFixed(2)} USD). View product details and buy from the AllChinaBuy Spreadsheet catalog.`;

  const head: HeadData = {
    title,
    description,
    keywords: `AllChinaBuy, ${product.category}, ${product.name}`,
    canonical,
    ogType: 'product',
    ogImage: product.image,
    ogImageAlt: product.name,
    locale: HOME_SEO[lang].locale,
  };

  applyHead(head);

  injectJsonLd('seo-product', {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.image,
    description,
    category: product.category,
    inLanguage: lang,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'CNY',
      price: product.priceYuan.toString(),
      availability: 'https://schema.org/InStock',
      url: product.url,
    },
  });
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const route = resolveRoute(location.pathname);

    if (route.type === 'notfound') {
      clearJsonLd();
      applyNotFound();
      return;
    }

    clearJsonLd();

    if (route.type === 'home') {
      applyHome(route.lang, route.canonicalPath);
    } else if (route.type === 'article') {
      applyArticle(route.lang, route.canonicalPath);
    } else if (route.type === 'product' && route.productId) {
      applyProduct(route.lang, route.canonicalPath, route.productId);
    }
  }, [location.pathname]);

  return null;
}